// Condición de recinto (ADR-0138) — wire shapes for the residential
// source of the asset's current state (ADR-0137 stateSource
// "area_condition"). The engine derivations live in
// @bitacorapro/core-engine's area-condition.ts; these are the contracts
// that cross the wire:
//
//   - reports.area_condition_snapshot entries (frozen at sign/close);
//   - PATCH /reports/:id/area-captures/:captureId/condition — the
//     inspector confirms or changes the AI's proposal;
//   - GET /assets/:id/area-state — «Estado del inmueble · última acta»;
//   - the per-area condition the public share (/r/<token>) renders.
import { areaConditionSourceSchema } from "@bitacorapro/core-engine";
import { z } from "zod";
import { assetVerdict } from "./asset-state.js";
// "legacy_finding": read from a pre-ADR-0138 «Buen estado» finding.
export const resolvedAreaConditionSourceSchema = z.union([
    areaConditionSourceSchema,
    z.literal("legacy_finding"),
]);
export const areaConditionSnapshotEntrySchema = z.object({
    areaName: z.string(),
    areaOrder: z.number().int(),
    conditionKey: z.string().nullable(),
    conditionSource: areaConditionSourceSchema.nullable(),
    conditionSummary: z.string().nullable(),
});
// ── PATCH /reports/:id/area-captures/:captureId/condition ─────────────
export const areaCaptureConditionParams = z.object({
    id: z.string().uuid(),
    captureId: z.string().uuid(),
});
export const areaCaptureConditionBody = z
    .object({
    // Must be one of the recipe's severity keys — checked server-side
    // against the org's recipe (400 INVALID_AREA_CONDITION otherwise).
    conditionKey: z.string().trim().min(1).max(64),
})
    .strict();
export const areaCaptureConditionResponse = z.object({
    id: z.string().uuid(),
    reportId: z.string().uuid(),
    areaName: z.string(),
    conditionKey: z.string(),
    conditionSource: z.literal("inspector"),
    conditionSummary: z.string().nullable(),
    conditionConfirmedAt: z.string(),
});
// ── GET /assets/:id/area-state ───────────────────────────────────────
export const assetAreaStatePhoto = z.object({
    url: z.string().url(),
    // The negative finding the photo backs, else the area's positive
    // condition, else null («Sin evaluar»).
    verdict: assetVerdict.nullable(),
    caption: z.string().nullable(),
});
export const assetAreaStateArea = z.object({
    areaName: z.string(),
    areaOrder: z.number().int(),
    condition: assetVerdict.nullable(),
    conditionSource: resolvedAreaConditionSourceSchema.nullable(),
    summary: z.string().nullable(),
    observationCount: z.number().int().nonnegative(),
    photos: z.array(assetAreaStatePhoto),
});
export const assetAreaStateResponse = z.object({
    assetId: z.string().uuid(),
    // Null when the asset has no terminal acta of a report type whose
    // recipe declares stateSource "area_condition".
    source: z
        .object({
        reportId: z.string().uuid(),
        reportType: z.string().min(1),
        // closedAt, or signedAt for signature closure.
        terminalAt: z.string(),
    })
        .nullable(),
    areas: z.array(assetAreaStateArea),
});
// ── Public share (/public/:token) ────────────────────────────────────
// Minimized on purpose: no capture id, no source, no timestamps.
// `photoUrls` are the area's own capture photos that back no observation
// — the photos that used to ride on the «Buen estado» finding, so the
// public acta keeps showing them once that finding stops existing.
export const publicAreaConditionSchema = z
    .object({
    areaName: z.string(),
    areaOrder: z.number().int(),
    conditionKey: z.string(),
    conditionSummary: z.string().nullable(),
    photoUrls: z.array(z.string().url()),
})
    .strict();
//# sourceMappingURL=area-condition.js.map